import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  makeStyles,
} from "@material-ui/core";
import { LcmDialogProps } from "./DialogProps";
import { Controller, useForm } from "react-hook-form";
import { nameof } from "ts-simple-nameof";
import { fieldLabels } from "../../sections/fieldlabels";
import { Error } from "../../common/Error";
import { MarkdownEditor } from "@ofqual/portal-components/components/MarkdownEditor";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import { caseNoteTypeLookupService } from "../../../services";
import { Select } from "@ofqual/portal-components/components/Select";
import { addCaseNote } from "../../../redux/actions";
import { useDispatch } from "react-redux";
import { CaseNoteModel } from "../../../domain/CaseNoteModel";
import { useState } from "react";
import { DatePicker } from "@ofqual/portal-components/components/DatePicker";
import { FileInput } from "@ofqual/portal-components/components/FileInput";

const defaultValues: Partial<CaseNoteModel> = {
  note: "",
  noteType: 0
};

const useStyles = makeStyles(() => ({
  actions: {
    padding: 15,
  },
}));

export const AddCaseNoteDialog: React.FC<LcmDialogProps> = ({
  lcmItem,
  onClose,
}) => {
  const { handleSubmit, control, errors } = useForm<CaseNoteModel>({
    defaultValues,
  });

  const classes = useStyles();
  const [caseNoteTypeLookups] = useLookup(caseNoteTypeLookupService);

  const [noteType, setNoteType] = useState(0);

  const dispatch = useDispatch();

  const onSubmit = (caseNote: CaseNoteModel) => {
    dispatch(addCaseNote(lcmItem.id, caseNote));
    onClose();
  };


  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-test="case-note-dialog"
    >
      <DialogTitle data-test="activity-title">Add case note</DialogTitle>
      <Divider component="div" />
      <DialogContent style={{ minHeight: "250px" }}>
        <form>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Controller
                control={control}
                name={nameof<CaseNoteModel>((x) => x.noteType)}
                rules={{ required: true }}
                render={({ onChange, value }) => (
                  <Select
                    variant="outlined"
                    label={fieldLabels.caseNoteType}
                    onChange={(e) => {
                      setNoteType(Number(e.target.value));
                      onChange(e.target.value);
                    }}
                    value={value}
                    required
                    data-test="case-note-type-field"
                  >
                    {caseNoteTypeLookups}
                  </Select>
                )}
              />
              {errors.noteType?.type === "required" && (
                <Error data-test="case-note-type-validation">
                  This field is required
                </Error>
              )}
            </Grid>

            <Grid item xs={12}>
              <Controller
                control={control}
                name={nameof<CaseNoteModel>((x) => x.note)}
                rules={{ required: true, maxLength: 5000 }}
                render={({ onChange, value }) => (
                  <>
                    <label>{fieldLabels.caseNote}*</label>
                    <MarkdownEditor
                      onChange={onChange}
                      value={value}
                      dataTest="case-note-text-field"
                    />
                  </>
                )}
              />
              {errors.note?.type === "required" && (
                <Error data-test="case-note-text-validation">
                  This field is required
                </Error>
              )}
              {errors.note?.type === "maxLength" && (
                <Error data-test="summary-length-validation">
                  Max length exceeded
                </Error>
              )}
            </Grid>

            {noteType === 1 && (
              <Grid item xs={12} sm={6}>
                <Controller
                  control={control}
                  rules={{ required: true }}
                  name={nameof<CaseNoteModel>((x) => x.meetingDate)}
                  as={
                    <DatePicker
                      label={fieldLabels.caseNoteMeetingDate}
                      dataTest="case-note-meeting-date"
                      clearable
                    />
                  }
                />
              </Grid>
            )}
          </Grid>
          <Grid item xs={12}>
            <Controller
              control={control}
              name={nameof<CaseNoteModel>((x) => x.documents)}
              as={
                <FileInput
                  multiple={true}
                  dataTest="create-note-add-attachment-button"
                />
              }
            />
          </Grid>
        </form>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button
          onClick={onClose}
          color="primary"
          data-test="close-add-note-dialog"
        >
          Close
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          color="primary"
          data-test="add-note-message-button"
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};
